import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { LineChart, Lightbulb, ShoppingBag, Users, CreditCard, ChevronDown, ChevronUp } from 'lucide-react';

interface RoadmapItem {
  title: string;
  description: string;
  details?: string;
  status: 'geplant' | 'in-entwicklung' | 'verfügbar';
  icon: React.ReactNode;
}

export function Roadmap() {
  const [expandedItems, setExpandedItems] = useState<number[]>([]);

  const toggleExpanded = (index: number) => {
    setExpandedItems(prev => 
      prev.includes(index) 
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const roadmapItems: RoadmapItem[] = [
    {
      title: 'Erweiterte Analysen',
      description: 'Detaillierte Monats- und Jahresübersichten deiner Kaufpausen und Einsparungen.',
      details: 'Graphische Auswertungen zeigen dir deine Sparfortschritte und Kaufmuster. Exportiere deine Daten als PDF oder CSV für deine persönliche Finanzplanung.',
      status: 'geplant',
      icon: <LineChart className="w-6 h-6" />
    },
    {
      title: 'Produktkategorien',
      description: 'Gruppiere deine Wunschprodukte nach individuellen Kategorien.',
      details: 'Erstelle eigene Kategorien wie "Technik", "Kleidung" oder "Haushalt". Erkenne Kaufmuster und setze kategorienspezifische Limits für bessere Budgetkontrolle.',
      status: 'geplant',
      icon: <ShoppingBag className="w-6 h-6" />
    },
    {
      title: 'Social-Login',
      description: 'Schnelle Anmeldung mit Google, Apple und anderen Anbietern.',
      details: 'Melde dich ganz einfach mit deinem bestehenden Google-, Apple- oder Microsoft-Konto an. Keine zusätzlichen Passwörter erforderlich - sicher und komfortabel.',
      status: 'in-entwicklung',
      icon: <Users className="w-6 h-6" />
    },
    {
      title: 'Weitere Zahlungsmethoden',
      description: 'Mehr Flexibilität bei der Bezahlung des Premium-Abonnements.',
      details: 'Neben PayPal werden SEPA-Lastschrift, Kreditkarte und weitere lokale Zahlungsmethoden unterstützt. Wähle die für dich passende Zahlungsart.',
      status: 'in-entwicklung',
      icon: <CreditCard className="w-6 h-6" />
    }
  ];

  const getStatusColor = (status: RoadmapItem['status']) => {
    switch (status) {
      case 'verfügbar':
        return 'bg-green-100 text-green-700 border-green-200 hover:bg-green-100 hover:text-green-700';
      case 'in-entwicklung':
        return 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100 hover:text-blue-700';
      case 'geplant':
        return 'bg-orange-100 text-orange-700 border-orange-200 hover:bg-orange-100 hover:text-orange-700';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200 hover:bg-gray-100 hover:text-gray-700';
    }
  };

  const getStatusText = (status: RoadmapItem['status']) => {
    switch (status) {
      case 'verfügbar':
        return 'Verfügbar';
      case 'in-entwicklung':
        return 'In Entwicklung';
      case 'geplant':
        return 'Geplant';
      default:
        return status;
    }
  };

  return (
    <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
          {roadmapItems.map((item, index) => {
          const isExpanded = expandedItems.includes(index);
          
          return (
          <Card 
            key={index}
            className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1 border-0 bg-gradient-to-br from-white to-gray-50"
          >
            <CardContent className="p-4 sm:p-6">
              <div className="flex items-start justify-between mb-3 sm:mb-4 gap-2">
                <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
                  <div className="p-2 sm:p-3 rounded-xl bg-emerald-100 text-emerald-600 group-hover:bg-emerald-600 group-hover:text-white transition-colors duration-300 flex-shrink-0">
                    {item.icon}
                  </div>
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-base sm:text-lg text-gray-900 group-hover:text-emerald-600 transition-colors leading-tight">
                      {item.title}
                    </h3>
                  </div>
                </div>
                <Badge 
                  className={`${getStatusColor(item.status)} border font-medium px-2 py-1 text-xs whitespace-nowrap flex-shrink-0`}
                >
                  {getStatusText(item.status)}
                </Badge>
              </div>
              
              <p className="text-gray-600 text-sm sm:text-base leading-relaxed mb-3 sm:mb-4">
                {item.description}
              </p>

                {item.details && (
                  <>
                    {isExpanded && (
                      <p className="text-gray-500 text-sm leading-relaxed mb-4">
                        {item.details}
                      </p>
                    )}
                    
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleExpanded(index)}
                      className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 p-0 h-auto font-medium"
                    >
                      {isExpanded ? (
                        <>
                          Weniger anzeigen
                          <ChevronUp className="w-4 h-4 ml-1" />
                        </>
                      ) : (
                        <>
                          Mehr erfahren
                          <ChevronDown className="w-4 h-4 ml-1" />
                        </>
                      )}
                    </Button>
                  </>
                )}
              
              {/* Progress indicator */}
              <div className="mt-4 w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-500 ${
                    item.status === 'verfügbar' ? 'bg-green-500 w-full' :
                    item.status === 'in-entwicklung' ? 'bg-blue-500 w-3/4' :
                    'bg-orange-500 w-1/4'
                  }`}
                />
              </div>
            </CardContent>
          </Card>
          );
        })}
    </div>
  );
} 